import React, { useEffect, useState } from 'react';
import PageApp from '../../../Page';
import { BankOutlined, EditOutlined, PlusOutlined, UploadOutlined, HomeOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Button, Drawer, Divider, Form, Input, message, Space, Switch, Table, Tag, Upload, Select } from 'antd';
import { getAllCompanies, updateCompany, getPaises, getEstadosByPais } from '../../../../apis/OdooApi';

const routes = [
    {
    href: '',
    title: (
      <>
        <HomeOutlined />
        <span>Inicio</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Configuración</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>General</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Compañías</span>
      </>
    ),
  },
];

const normalizeLogoSrc = (logo) => {
  if (!logo) {
    return null;
  }

  if (logo.startsWith('data:')) {
    return logo;
  }

  return `data:image/png;base64,${logo}`;
};

const CompaniasPage = () => {
  const [companies, setCompanies] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [logoValue, setLogoValue] = useState(null);
  const [paises, setPaises] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedPaisId, setSelectedPaisId] = useState(null);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      const response = await getAllCompanies();
      if (response.ok) {
        setCompanies(response.payload || []);
      } else {
        message.error(response.message || 'Error al cargar las empresas');
      }
    } catch (error) {
      message.error('Error de conexión al cargar las empresas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
    loadPaises();
  }, []);

  const loadPaises = async () => {
    try {
      const response = await getPaises();
      if (response.ok) {
        setPaises(response.payload || []);
      }
    } catch (error) {
      console.error('Error cargando países', error);
    }
  };

  const loadEstadosByPais = async (paisId) => {
    if (!paisId) {
      setEstados([]);
      return;
    }
    try {
      const response = await getEstadosByPais(paisId);
      if (response.ok) {
        setEstados(response.payload || []);
      }
    } catch (error) {
      console.error('Error cargando estados', error);
    }
  };

  const openEditor = (company = null) => {
    setSelectedCompany(company);
    setLogoValue(normalizeLogoSrc(company?.logo));
    setSelectedPaisId(company?.country_id || null);
    form.setFieldsValue({
      odoo_id: company?.odoo_id,
      name: company?.name,
      vat: company?.vat,
      representante: company?.representante,
      street: company?.street,
      street2: company?.street2,
      city: company?.city,
      state_id: company?.state_id,
      state_text: company?.state_text,
      zip: company?.zip,
      country_id: company?.country_id,
      country_text: company?.country_text,
      activo: company?.activo ?? true,
    });
    if (company?.country_id) {
      loadEstadosByPais(company.country_id);
    }
    setDrawerOpen(true);
  };

  const closeEditor = () => {
    setDrawerOpen(false);
    setSelectedCompany(null);
    setLogoValue(null);
    setSelectedPaisId(null);
    setEstados([]);
    form.resetFields();
  };

  const handlePaisChange = (paisId) => {
    setSelectedPaisId(paisId);
    form.setFieldsValue({ state_id: null });
    loadEstadosByPais(paisId);
  };

  const beforeLogoUpload = async (file) => {
    if (!file.type.startsWith('image/')) {
      message.error('El logo debe ser una imagen válida');
      return Upload.LIST_IGNORE;
    }

    const logoBase64 = await fileToBase64(file);
    setLogoValue(logoBase64);
    return false;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        ...selectedCompany,
        ...values,
        logo: logoValue,
        activo: values.activo,
      };

      const response = await updateCompany(payload);
      if (response.ok) {
        message.success(selectedCompany ? 'Empresa actualizada' : 'Empresa creada');
        closeEditor();
        await refreshData();
      } else {
        message.error(response.message || 'No se pudo guardar la empresa');
      }
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error('No se pudo guardar la empresa');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'odoo_id',
      key: 'odoo_id',
      width: 120,
    },
    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      width: 110,
      render: (logo) => {
        const src = normalizeLogoSrc(logo);
        return src ? (
          <img
            src={src}
            alt="Logo"
            style={{ height: 40, maxWidth: 90, objectFit: 'contain' }}
          />
        ) : (
          <span style={{ color: '#999' }}>Sin logo</span>
        );
      },
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'RFC',
      dataIndex: 'vat',
      key: 'vat',
      render: (vat) => (vat ? <Tag color="blue">{vat}</Tag> : '-'),
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      key: 'activo',
      width: 120,
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>{activo ? 'Activa' : 'Inactiva'}</Tag>
      ),
    },
    {
      title: 'Acciones',
      dataIndex: 'actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button icon={<EditOutlined />} onClick={() => openEditor(record)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <PageApp routes={routes} title={"Compañías"}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>
          Nueva compañía
        </Button>
      </Space>

      <Divider />

      <Table
        dataSource={companies}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: 'No hay companias registradas' }}
      />

      <Drawer
        title={selectedCompany ? 'Editar compania' : 'Nueva compania'}
        open={drawerOpen}
        onClose={closeEditor}
        width={720}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={closeEditor}>Cancelar</Button>
            <Button type="primary" loading={saving} onClick={handleSubmit}>
              Guardar
            </Button>
          </Space>
        }
      >
        <Form layout="vertical" form={form} preserve={false}>
          <Form.Item
            label="Nombre"
            name="name"
            rules={[{ required: true, message: 'Ingresa el nombre' }]}
          >
            <Input placeholder="Nombre de la empresa" />
          </Form.Item>

          <Form.Item
            label="RFC"
            name="vat"
            rules={[{ required: true, message: 'Ingresa el RFC' }]}
          >
            <Input placeholder="RFC" />
          </Form.Item>

          <Form.Item label="Representante legal" name="representante">
            <Input placeholder="Nombre del representante legal o responsable" />
          </Form.Item>

          <Form.Item label="Calle" name="street">
            <Input placeholder="Calle" />
          </Form.Item>

          <Form.Item label="Calle 2" name="street2">
            <Input placeholder="Colonia, interior o referencia" />
          </Form.Item>

          <Form.Item label="Ciudad" name="city">
            <Input placeholder="Ciudad" />
          </Form.Item>

          <Form.Item label="Código Postal" name="zip">
            <Input placeholder="Código Postal" />
          </Form.Item>

          <Form.Item label="País" name="country_id">
            <Select
              placeholder="Selecciona un país"
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={handlePaisChange}
              allowClear
            >
              {paises.map((p) => (
                <Select.Option key={p.value} value={p.value}>
                  {p.text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Estado" name="state_id">
            <Select
              placeholder="Selecciona un estado"
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
              disabled={!selectedPaisId}
              allowClear
            >
              {estados.map((e) => (
                <Select.Option key={e.value} value={e.value}>
                  {e.text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Activo" name="activo" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item label="Logo institucional">
            <Space direction="vertical" style={{ width: '100%' }}>
              {logoValue ? (
                <img
                  src={logoValue}
                  alt="Logo empresa"
                  style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain', border: '1px solid #f0f0f0', padding: 8 }}
                />
              ) : (
                <div style={{ border: '1px dashed #d9d9d9', padding: 16, color: '#999' }}>
                  Sin logo cargado
                </div>
              )}
              <Upload beforeUpload={beforeLogoUpload} showUploadList={false} accept="image/*">
                <Button icon={<UploadOutlined />}>Cargar logo</Button>
              </Upload>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </PageApp>
  );
};

export default CompaniasPage;
